// Общие поля: номер записи, время, дата, версия записи (ADR-0008, contracts/api.md «Общие правила»).
import { z } from './zod.ts';

/** Номер записи — uuid (в базе v7). */
export const IdSchema = z.uuid();
export type Id = z.infer<typeof IdSchema>;

/** Момент времени в ответах — ISO 8601 в UTC: 2026-10-14T10:42:00.000Z. Показ — в поясе установки. */
export const TimestampSchema = z.iso.datetime();

/** Момент времени в запросе: UTC или со смещением — 2026-10-14T00:00:00+05:00. */
export const TimestampInputSchema = z.iso.datetime({ offset: true });

/** Дата без времени: 2026-10-20 (сроки назначений и замещений). */
export const DateSchema = z.iso.date();

/** Версия записи: растёт при каждом изменении, передаётся в If-Match. */
export const VersionSchema = z.number().int().min(1);

/**
 * Автор правок системы в истории пункта и ленте «Изменения» (027: research, Р12): разовый перенос
 * без пользователя. Пользователя с этим номером нет — войти им нельзя, ролей у него нет.
 */
export const SYSTEM_ACTOR_ID = '00000000-0000-4000-8000-000000000027';
/** Подпись системного автора в истории и окне конфликта. */
export const SYSTEM_ACTOR_NAME = 'Система';

/** Человек в ответе: номер и ФИО из текущей записи пользователя (FR-033). */
export const PersonRefSchema = z.object({ id: IdSchema, fullName: z.string() });
export type PersonRef = z.infer<typeof PersonRefSchema>;

/** Параметр пути {id}. */
export const IdParamsSchema = z.object({ id: IdSchema });

/** Изменение записи — с If-Match: <version>; устаревшая версия → 409 REC-001. */
export const IfMatchHeadersSchema = z.object({
  'if-match': z.coerce.number().int().min(1),
});

/**
 * Номер операции (FR-025 – FR-027; 002, research, пункт 5): 12 знаков Base32 Крокфорда —
 * цифры и латинские буквы без I, L, O, U, 60 случайных бит. Создаёт только сервер.
 */
export const OPERATION_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
export const OPERATION_LENGTH = 12;
export const OPERATION_PATTERN = /^[0-9A-HJKMNP-TV-Z]{12}$/;
export const OperationIdSchema = z.string().regex(OPERATION_PATTERN);

/**
 * Номер операции из поиска: без дефисов и пробелов, заглавными; похожие буквы — как в Base32
 * Крокфорда: O — ноль, I и L — единица. Номер, продиктованный по телефону, находится.
 */
export function normalizeOperation(text: string): string {
  return text.toUpperCase().replace(/[\s-]/g, '').replace(/O/g, '0').replace(/[IL]/g, '1');
}

/** Номер операции на экране — группами по четыре: 7F3K-92QD-1A2B. */
export function formatOperation(id: string): string {
  return id.match(/.{1,4}/g)?.join('-') ?? id;
}

/** Заголовок с номером операции — на каждом ответе сервера (contracts/api.md 002). */
export const OPERATION_HEADER = 'x-kp-operation';

/** Заголовок, без которого изменяющий запрос отклоняется (FR-012а, SYS-002). */
export const REQUEST_HEADER = 'x-kp-request';
/** Заголовок фоновых запросов: они не продлевают сеанс (FR-008б). */
export const BACKGROUND_HEADER = 'x-kp-background';
/** Cookie сеанса (research, пункт 8). */
export const SESSION_COOKIE = '__Host-sid';
